// ==================== IMPORTS ====================
import axios from "axios";
import { io } from "socket.io-client";

// ==================== CONFIG ====================
const API_URL = process.env.REACT_APP_API_URL;

// ==================== TOKEN HELPER ====================
/**
 * Lấy token đăng nhập từ cookie
 */
// Helper: lấy token từ localStorage
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");  // Thay từ Cookies.get sang localStorage.getItem
  if (!token) throw new Error("Chưa đăng nhập hoặc token không tồn tại");
  return { Authorization: `Bearer ${token}` };
};

// =====================================================
// =============== API CALL FUNCTIONS ==================
// =====================================================

/**
 * ✅ Tạo lịch xem phòng
 * @param {Object} scheduleData
 *  {
 *    room_id,
 *    customer_name,
 *    customer_phone,
 *    start_time,
 *    end_time,
 *    note
 *  }
 */
export const createSchedule = async (scheduleData) => {
  try {
    const res = await axios.post(`${API_URL}/schedules`, scheduleData, {
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (err) {
    console.error("❌ Lỗi khi tạo lịch:", err.response?.data || err.message);
    throw err.response?.data || { error: "Không thể tạo lịch hẹn" };
  }
};

/**
 * ✅ Lấy toàn bộ lịch (Admin/Assistant/Boss/CTV tuỳ quyền)
 */
export const getSchedules = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    if (params.page) query.append("page", params.page);
    if (params.limit) query.append("limit", params.limit);
    if (params.status && params.status !== "all") query.append("status", params.status);
    if (params.buildingId) query.append("buildingId", params.buildingId);
    if (params.roomId) query.append("roomId", params.roomId);
    if (params.search) query.append("search", params.search);
    if (params.dateFrom) query.append("dateFrom", params.dateFrom);
    if (params.dateTo) query.append("dateTo", params.dateTo);

    const res = await axios.get(
      `${API_URL}/schedules?${query.toString()}`,
      { headers: getAuthHeaders() }
    );
    return res.data;
  } catch (err) {
    console.error("❌ Lỗi khi lấy danh sách lịch:", err);
    throw err;
  }
};

/**
 * ✅ Cập nhật lịch (phân công, thay đổi trạng thái, thêm ghi chú)
 * @param {String} id
 * @param {Object} updateData
 */
export const updateSchedule = async (id, updateData) => {
  try {
    const res = await axios.put(`${API_URL}/schedules/${id}`, updateData, {
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (err) {
    console.error("❌ Lỗi khi cập nhật lịch:", err.response?.data || err.message);
    throw err.response?.data || { error: "Không thể cập nhật lịch" };
  }
};

/**
 * ✅ Xoá lịch hẹn
 * @param {String} id
 */
export const deleteSchedule = async (id) => {
  try {
    const res = await axios.delete(`${API_URL}/schedules/${id}`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (err) {
    console.error("❌ Lỗi khi xoá lịch:", err.response?.data || err.message);
    throw err.response?.data || { error: "Không thể xoá lịch" };
  }
};

/**
 * ✅ Lấy danh sách lịch của người dùng hiện tại
 */
export const getMySchedules = async () => {
  try {
    const res = await axios.get(`${API_URL}/schedules/me`, {
      headers: getAuthHeaders(),
    });
    return res.data.data;
  } catch (err) {
    console.error("❌ Lỗi khi lấy lịch của tôi:", err.response?.data || err.message);
    throw err.response?.data || { error: "Không thể tải lịch cá nhân" };
  }
};

/**
 * ✅ Kiểm tra nhân sự có rảnh hay không
 * @param {String} staffId
 * @param {String} start_time (ISO String)
 * @param {Number} duration (minutes, default = 30)
 */
export const checkStaffAvailability = async (
  staffId,
  start_time,
  duration = 30
) => {
  try {
    const res = await axios.get(
      `${API_URL}/schedules/staff/${staffId}/availability`,
      {
        params: { time: start_time, duration },
        headers: getAuthHeaders(),
      }
    );
    return res.data; // { available: boolean, conflicts: [...] }
  } catch (err) {
    console.error("❌ Lỗi khi kiểm tra thời gian trống:", err.response?.data || err.message);
    throw err.response?.data || { error: "Không thể kiểm tra lịch nhân sự" };
  }
};

/**
 * ✅ Lấy danh sách khung giờ bận theo phòng (BusySlot)
 * @param {String} room_id
 * @param {String|Date} date - YYYY-MM-DD hoặc đối tượng Date
 */
export const getBusySlotsByRoom = async (roomId, date) => {
  try {
    const res = await axios.get(`${API_URL}/busy-slots/room/${roomId}`, {
      headers: getAuthHeaders(),
      params: { date },
    });
    console.log("🟢 API /busy-slots/room result:", res.data);
    return res.data;
  } catch (err) {
    console.error("❌ Lỗi khi lấy BusySlots:", err.response?.data || err.message);
    throw err.response?.data || "Lỗi lấy khung giờ bận";
  }
};
//  lấy thống kê số lượng lịch theo trạng thái
export const getScheduleStats = async () => {
  try {
    const res = await axios.get(
      `${API_URL}/schedules/stats`,
      { headers: getAuthHeaders() }
    );
    return res.data;
  } catch (err) {
    console.error("❌ Lỗi khi lấy thống kê lịch:", err);
    throw err;
  }
};

// =====================================================
// =============== REALTIME (Socket.IO) ================
// =====================================================

// ⚙️ Tạo kết nối socket
const socket = io(API_URL, {
  transports: ["websocket"],
  autoConnect: true,
});

/**
 * ✅ Đăng ký lắng nghe sự kiện realtime của lịch
 * @param {Function} onCreated - callback khi có lịch mới
 * @param {Function} onUpdated - callback khi lịch được cập nhật
 * @param {Function} onDeleted - callback khi lịch bị xoá
 */
export const subscribeSchedules = (onCreated, onUpdated, onDeleted) => {
  if (onCreated) socket.on("scheduleCreated", onCreated);
  if (onUpdated) socket.on("scheduleUpdated", onUpdated);
  if (onDeleted) socket.on("scheduleDeleted", onDeleted);
};

/**
 * ✅ Hủy lắng nghe sự kiện realtime
 */
export const unsubscribeSchedules = () => {
  socket.off("scheduleCreated");
  socket.off("scheduleUpdated");
  socket.off("scheduleDeleted");
};

/**
 * ✅ Xuất socket để dùng chung (nếu cần global listener)
 */
export { socket };
